import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import SEOHead from './SEOHead';
import RelatedArticles from './RelatedArticles';
import '../styles/articlepage.css';
import AOS from 'aos';
import 'aos/dist/aos.css';

const ArticlePage = () => {
    const { articleSlug } = useParams();
    const [article, setArticle] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const processArticleContent = (content) => {
        // Handle rich-text format
        if (Array.isArray(content) && content.length > 0 && content[0].type === 'rich-text') {
            return content
                .sort((a, b) => a.order - b.order)
                .map(section => section.content)
                .join('');
        } 
        
        // Handle sections format
        if (content?.sections && Array.isArray(content.sections)) {
            return content.sections.map(section => {
                switch (section.type) {
                    case 'heading':
                        return `<h${section.level}>${section.content}</h${section.level}>`;
                    case 'paragraph':
                        return `<p>${section.content}</p>`;
                    case 'list':
                        const listTag = section.style === 'ordered' ? 'ol' : 'ul';
                        const items = section.items.map(item => `<li>${item}</li>`).join('');
                        return `<${listTag}>${items}</${listTag}>`;
                    case 'image':
                        return `<figure>
                            <img src="${section.url}" alt="${section.alt || ''}" style="max-width: 100%; height: auto;" />
                            ${section.caption ? `<figcaption>${section.caption}</figcaption>` : ''}
                        </figure>`;
                    default:
                        return '';
                }
            }).join('');
        }

        // Handle image sections in rich-text format
        if (Array.isArray(content)) {
            return content.map(section => {
                if (section.type === 'image') {
                    return `<figure>
                        <img src="${section.url}" alt="${section.alt || ''}" style="max-width: 100%; height: auto;" />
                        ${section.caption ? `<figcaption>${section.caption}</figcaption>` : ''}
                    </figure>`;
                }
                return section.content || '';
            }).join('');
        }

        // Fallback for unknown format
        return JSON.stringify(content, null, 2);
    };

    useEffect(() => {
        AOS.init({ duration: 800, easing: 'ease-in-out', once: true });

        const fetchArticle = async () => {
            if (!articleSlug) {
                setError('No article slug provided');
                setIsLoading(false);
                return;
            }

            try {
                console.log('Current article slug:', articleSlug);

                const { data, error } = await supabase
                    .from('articles')
                    .select('*, category:categories(name, slug)')
                    .eq('slug', articleSlug.trim())
                    .maybeSingle();

                if (error) {
                    console.error('Error fetching specific article:', error);
                    throw error;
                }

                if (!data) {
                    console.log('No article found for slug:', articleSlug);
                    setError('Article not found');
                } else {
                    console.log('Found article:', data);
                    
                    // Process the content
                    const processedContent = processArticleContent(data.content);
                    
                    setArticle({
                        ...data,
                        content: processedContent
                    });
                }
            } catch (error) {
                console.error('Error in fetchArticle:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchArticle();
    }, [articleSlug]);

    if (isLoading) {
        return <div className="loading">Loading...</div>;
    }

    if (error) {
        return (
            <div className="error-message">
                <h2>Error loading article</h2>
                <p>{error}</p>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="error-message">
                <h2>Article Not Found</h2>
                <p>Could not find an article with slug: {articleSlug}</p>
            </div>
        );
    }

    return (
        <article className="article-container">
            <SEOHead
                title={article.title}
                description={article.description}
                path={`/article/${article.slug}`}
                image={article.featured_image}
                article={true}
                publishedAt={article.published_at}
                updatedAt={article.updated_at}
                author={article.author?.name}
                category={article.category?.name}
            />
            <div 
                className="article-header" 
                style={{
                    backgroundImage: article.featured_image
                        ? `url(${article.featured_image})`
                        : `url(/assets/featured-bg/${article.slug}.jpg), url(/assets/featured-bg/${article.slug}.png)`
                }}
            >
                <div className="header-content">
                    <div className="header-content-inner">
                        <h1 className="article-title" data-aos="fade-up">{article.title}</h1>
                        <div className="article-meta" data-aos="fade-up" data-aos-delay="100">
                            <span className="category">{article.category?.name}</span>
                            {article.published_at && (
                                <span className="date">
                                    {new Date(article.published_at).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="article-content">
                <div
                    className="article-body"
                    data-aos="fade-up"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                />
            </div>
            <RelatedArticles
                categoryId={article.category_id}
                currentId={article.id}
                categorySlug={article.category?.slug}
                categoryName={article.category?.name}
            />
        </article>
    );
};

export default ArticlePage;
